// OMD-194: 이메일 인쇄 구현(host). 뷰어(email-thread-viewer)에서 이식.
// 뷰어가 WebToNative.print 로 보낸 PrintPayload + owma 자원(로고/아이콘)으로 인쇄 문서를 조립하고
// 새 창을 띄워 인쇄한다. 뷰어는 더 이상 window.open / DOM 조립 / print() 를 알지 못한다.

import i18n from 'i18n'
// meetingInfo.timezone 경로에서 `.tz()` 를 쓰므로 tz-augmented moment 를 직접 import 한다
// (뷰어 EmailView/uiExt.tsx 와 동일 패턴). `import moment from 'moment'` 는 desktop alias 상
// moment-timezone 이 augment 하는 인스턴스와 달라 `.tz` 가 없다.
import moment from 'moment-timezone'

import { getImageInfo } from 'components/FTIcon/getImageInfo'
import { applyPseudoStyleScope } from 'libs/style-to-inline'
import { ClientJS } from '../../clientjslite'

import { formatBytes } from './core'

// 수신 메일 본문의 Windows 한글 폰트(굴림체 등) macOS 명조 폴백 교정 별칭(OMD-198)을 인쇄 문서에도
// 재사용한다. 인쇄는 window.open 으로 별도 document 를 조립해 owma 전역 CSS 가 닿지 않으므로, 화면 뷰어와
// 동일 소스(fontAliases.css)를 문자열로 가져와(?inline) 인쇄 head 에 주입한다. host 소유 사본
// (remove-email-thread-viewer P1) — common-web → email-thread-viewer 역참조 제거.
import fontAliasesCss from './fontAliases.css?inline'

import type { PrintPayload, PrintEmailAddress, PrintAttachment } from './printPayload'

export interface PrintEmailOptions extends PrintPayload {
  logoSrc: string
  calendarIconSrc: string
}

// Safari(WebKit 엔진) 계열 판별. @page 여백 등 WebKit 인쇄 고유 처리 분기에 사용한다.
// 판별 로직은 clientjslite(ClientJS.isSafari)로 통합했다(DocViewer 와 단일 소스 공유).
const isSafari = new ClientJS().isSafari()

// HTML 문자열(뷰어가 sanitize 한 본문 / getDisplayEmailHTMLStr 결과)을 파싱해 target 에 붙인다.
// innerHTML 직접 할당 대신 DOMParser + importNode 로 안전하게 이식(원본 cloneNode 동작과 동등).
function appendHtmlString(doc: Document, target: HTMLElement, html: string): void {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  // 파서가 <head> 로 hoist 한 <style>(의사요소 보존 스타일 등)도 함께 이식.
  parsed.head.querySelectorAll('style').forEach((node) => {
    target.appendChild(doc.importNode(node, true))
  })
  Array.from(parsed.body.childNodes).forEach((node) => {
    target.appendChild(doc.importNode(node, true))
  })
}

export function buildPrintDocument(doc: Document, options: PrintEmailOptions): void {
  const {
    from,
    to: toList,
    cc: ccList,
    bcc: bccList,
    receivedTime,
    attachments,
    dateTimeFormat,
    timeFormat,
    meetingInfo,
    fromDisplayHtml,
    senderDisplayHtml,
    userEmail,
    bodyHtml,
    logoSrc,
    calendarIconSrc,
  } = options

  const subject = options.subject || `(${i18n.t('email.desc.noSubject')})`
  const dateStr = moment(receivedTime * 1000).format(dateTimeFormat)

  doc.title = subject

  const meta = doc.createElement('meta')
  meta.setAttribute('charset', 'utf-8')
  doc.head.appendChild(meta)

  // OMD-198: 본문의 Windows 한글 폰트(굴림체 등)가 인쇄 문서(별도 document)에서 macOS 명조로
  // 폴백되지 않도록, 화면 뷰어와 동일한 @font-face 별칭을 인쇄 head 에 주입한다(본문 style 보다 앞).
  const fontAliasStyle = doc.createElement('style')
  fontAliasStyle.textContent = fontAliasesCss
  doc.head.appendChild(fontAliasStyle)

  const style = doc.createElement('style')
  style.textContent = [
    `body {`,
    `  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;`,
    `  font-size: 14px; color: #262626; margin: 0; padding: 20px 0; line-height: 1.5;`,
    `}`,
    `.print-section { padding: 0 20px; }`,
    `.print-logo { margin-bottom: 12px; }`,
    `.print-logo img { height: 18px; }`,
    `.print-subject { font-size: 16px; font-weight: 600; margin: 0 0 12px 0; word-break: break-word; }`,
    `.print-meta { border-collapse: collapse; width: 100%; font-size: 14px; color: #1e1e1e; line-height: 1.5; }`,
    `.print-meta td { padding: 2px 0; vertical-align: top; word-break: break-word; }`,
    `.print-meta .print-meta-label { width: 1%; white-space: nowrap; padding-right: 8px; color: #1e1e1e; font-weight: 400; }`,
    `.print-meta .print-meta-from { font-weight: 700; font-size: 14px; }`,
    `.print-meta .print-meta-date { font-size: 12px; color: #6b7280; font-weight: 400; text-align: right; white-space: nowrap; }`,
    `hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }`,
    `.print-divider { border: none; border-top: 1px solid #E5E7EB; margin: 16px 0; }`,
    `.print-attachments { font-size: 11px; color: #1e1e1e; margin-bottom: 16px; word-break: break-word; border-collapse: collapse; }`,
    `.print-attachments img { vertical-align: middle; }`,
    `.print-attachments b { font-size: 11px; }`,
    `.print-att-size { font-size: 9px; color: #6b7280; }`,
    `.print-ical { margin: 12px 0; padding-top: 10px; padding-bottom: 10px; background: #f9fafb; border: 1px solid #E5E7EB; border-radius: 6px; }`,
    `.print-ical-table { border-collapse: collapse; }`,
    `.print-ical-icon { vertical-align: top; padding-right: 12px; width: 48px; }`,
    `.print-ical-icon-wrapper { position: relative; width: 48px; height: 48px; }`,
    `.print-ical-icon-wrapper img { display: block; }`,
    `.print-ical-date { position: absolute; top: 12px; left: 0; width: 100%; text-align: center; font-size: 13px; font-weight: 700; color: #4c31cb; line-height: 32px; }`,
    `.print-ical-title { font-size: 14px; font-weight: 700; color: #1e1e1e; padding-bottom: 2px; vertical-align: bottom; }`,
    `.print-ical-info { font-size: 13px; color: #6b7280; padding-top: 1px; vertical-align: top; }`,
    `.print-ical-actions { padding-top: 8px; }`,
    `.print-ical-btn + .print-ical-btn { margin-left: 12px; }`,
    `.print-ical-btn { display: inline-block; min-width: 100px; height: 38px; line-height: 36px; padding: 0 16px; text-align: center; border-radius: 6px; border: 1px solid #d1d5db; background: #fff; font-size: 14px; color: #374151; box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }`,
    `.print-ical-btn.accept { color: #007aff; border-color: #007aff; }`,
    `.print-ical-btn.decline { color: #fd4c45; border-color: #fd4c45; }`,
    `.print-ical-btn.tentative { color: #666; border-color: #666; }`,
    `.print-body { line-height: initial; font-size: initial; }`,
    `.print-body *, .print-body ::after, .print-body ::before { box-sizing: initial; }`,
    `.print-body p { margin: initial; }`,
    `.print-body blockquote { margin: revert; }`,
    `.print-body a { text-decoration: underline; }`,
    `.print-body pre { white-space: pre-wrap; word-break: break-word; }`,
    `.print-body table { max-width: 100%; }`,
    `.print-body img, .print-body svg { max-width: 100%; height: auto; }`,
    `@media print {`,
    `  body { padding: 0; margin: 0; }`,
    // OMD-203: Safari 는 @page 에 명시적 길이 margin 이 있으면 미리보기는 대화상자 기본 여백으로,
    // 실제 인쇄/PDF 는 명시값으로 페이지 영역을 계산해 미리보기 페이지 수와 출력이 어긋난다(WebKit 버그).
    // auto 를 쓰면 미리보기·출력이 같은 기본 여백을 사용해 일치한다. Chrome 은 정상이라 1.2cm 를 유지한다.
    // (되돌리기 전 주의: 1.2cm 로 바꾸면 Safari 미리보기 페이지 수 불일치가 재발한다.)
    `  @page { margin: ${isSafari ? 'auto' : '1.2cm'}; }`,
    `  .print-body img, .print-body svg {`,
    `    max-width: 100% !important; height: auto !important; page-break-inside: avoid;`,
    `  }`,
    `}`,
  ].join('\n')
  doc.head.appendChild(style)

  const formatRecipient = (r: PrintEmailAddress): string => {
    if (r.name && r.name !== r.email) {
      return `${r.name} <${r.email}>`
    }
    return `<${r.email}>`
  }

  // 로고 섹션
  if (logoSrc) {
    const logoSection = doc.createElement('div')
    logoSection.className = 'print-section'
    const logoDiv = doc.createElement('div')
    logoDiv.className = 'print-logo'
    const logoImg = doc.createElement('img')
    logoImg.src = logoSrc
    logoImg.alt = 'logo'
    logoDiv.appendChild(logoImg)
    logoSection.appendChild(logoDiv)
    doc.body.appendChild(logoSection)
  }

  const hrBefore = doc.createElement('hr')
  hrBefore.className = 'print-divider'
  doc.body.appendChild(hrBefore)

  const subjectSection = doc.createElement('div')
  subjectSection.className = 'print-section'
  const subjectH1 = doc.createElement('h1')
  subjectH1.className = 'print-subject'
  subjectH1.textContent = subject
  subjectSection.appendChild(subjectH1)
  doc.body.appendChild(subjectSection)

  const hrAfter = doc.createElement('hr')
  hrAfter.className = 'print-divider'
  doc.body.appendChild(hrAfter)

  const metaTable = doc.createElement('table')
  metaTable.className = 'print-meta'

  const addMetaRow = (label: string, value: string, className?: string) => {
    const tr = doc.createElement('tr')
    const tdLabel = doc.createElement('td')
    tdLabel.className = 'print-meta-label'
    tdLabel.textContent = `${label}:`
    const tdValue = doc.createElement('td')
    tdValue.textContent = value
    if (className) tdValue.className = className
    tr.appendChild(tdLabel)
    tr.appendChild(tdValue)
    return tr
  }

  // 1행: From + Date (Figma: from bold 14px, date 12px #6b7280 right-aligned)
  const fromRow = doc.createElement('tr')
  const fromValueTd = doc.createElement('td')
  fromValueTd.className = 'print-meta-from'
  fromValueTd.setAttribute('colspan', '2')
  fromValueTd.textContent = formatRecipient(from)
  const dateTd = doc.createElement('td')
  dateTd.className = 'print-meta-date'
  dateTd.textContent = dateStr
  fromRow.appendChild(fromValueTd)
  fromRow.appendChild(dateTd)
  metaTable.appendChild(fromRow)

  // To, Cc, Bcc rows
  if (toList.length > 0) {
    metaTable.appendChild(
      addMetaRow(i18n.t('email.field.to'), toList.map(formatRecipient).join('; ')),
    )
  }
  if (ccList.length > 0) {
    metaTable.appendChild(
      addMetaRow(i18n.t('email.field.cc'), ccList.map(formatRecipient).join('; ')),
    )
  }
  if (bccList.length > 0) {
    metaTable.appendChild(
      addMetaRow(i18n.t('email.field.bcc'), bccList.map(formatRecipient).join('; ')),
    )
  }
  const metaSection = doc.createElement('div')
  metaSection.className = 'print-section'
  metaSection.appendChild(metaTable)
  doc.body.appendChild(metaSection)

  // 캘린더(미팅) 정보
  if (meetingInfo != null) {
    const startTime = meetingInfo.timezone
      ? moment(meetingInfo.dt_start).tz(meetingInfo.timezone)
      : moment(meetingInfo.dt_start)
    const endTime = meetingInfo.timezone
      ? moment(meetingInfo.dt_end).tz(meetingInfo.timezone)
      : moment(meetingInfo.dt_end)

    const rangeStr = startTime.isSame(endTime, 'day')
      ? `${startTime.format(dateTimeFormat)} - ${endTime.format(timeFormat)}`
      : `${startTime.format(dateTimeFormat)} - ${endTime.format(dateTimeFormat)}`

    const startDateStr = startTime.format('MM.DD')

    // 타이틀 생성 (뷰어 uiExt.getICalElem / buildPrintDocument 로직 이식)
    let icalTitle = ''
    const senderName = senderDisplayHtml
    const fromName = fromDisplayHtml
    const isOwnerMeeting =
      (meetingInfo.type === 0 && meetingInfo.sender.email === userEmail) ||
      (meetingInfo.type !== 0 && meetingInfo.sender.email !== userEmail)

    if (meetingInfo.type === 1) {
      const canceledStr = i18n.t('meeting.title.canceled', { summary: '' })
      icalTitle =
        meetingInfo.title.indexOf(canceledStr) !== 0
          ? i18n.t('meeting.title.canceled', { summary: meetingInfo.title })
          : meetingInfo.title
    } else if (!isOwnerMeeting) {
      let statusTxt = ''
      if (meetingInfo.type === 2) statusTxt = i18n.t('meeting.status.accepted')
      else if (meetingInfo.type === 3) statusTxt = i18n.t('meeting.status.tentative')
      else if (meetingInfo.type === 4) statusTxt = i18n.t('meeting.status.declined')

      icalTitle =
        meetingInfo.sequence != null && meetingInfo.sequence > 0
          ? i18n.t('meeting.title.updated', { organizer: fromName })
          : i18n.t('meeting.title.invited', { organizer: fromName, status: statusTxt })
    } else {
      if (meetingInfo.type === 2)
        icalTitle = i18n.t('meeting.title.accepted', { attendee: fromName })
      else if (meetingInfo.type === 3)
        icalTitle = i18n.t('meeting.title.tentative', { attendee: fromName })
      else if (meetingInfo.type === 4)
        icalTitle = i18n.t('meeting.title.declined', { attendee: fromName })
      else icalTitle = i18n.t('meeting.title.invited', { organizer: senderName, status: '' })
    }

    const hasExtraRow = meetingInfo.recurrence || meetingInfo.location
    const hasActionsRow = meetingInfo.enable_response === true && !isOwnerMeeting
    const iconRowspan = 2 + (hasExtraRow ? 1 : 0) + (hasActionsRow ? 1 : 0)

    // 캘린더 아이콘 + 텍스트 테이블
    const icalSection = doc.createElement('div')
    icalSection.className = 'print-section print-ical'

    const icalTable = doc.createElement('table')
    icalTable.setAttribute('cellspacing', '0')
    icalTable.setAttribute('cellpadding', '0')
    icalTable.setAttribute('border', '0')
    icalTable.className = 'print-ical-table'
    const icalTbody = doc.createElement('tbody')
    const icalRow = doc.createElement('tr')

    // 캘린더 아이콘 셀 (아이콘 위에 날짜 겹침)
    const iconTd = doc.createElement('td')
    iconTd.className = 'print-ical-icon'
    iconTd.setAttribute('rowspan', String(iconRowspan))
    const iconWrapper = doc.createElement('div')
    iconWrapper.className = 'print-ical-icon-wrapper'
    const calImg = doc.createElement('img')
    calImg.src = calendarIconSrc
    calImg.setAttribute('width', '48')
    calImg.setAttribute('height', '48')
    iconWrapper.appendChild(calImg)
    const dateOverlay = doc.createElement('div')
    dateOverlay.className = 'print-ical-date'
    dateOverlay.textContent = startDateStr
    iconWrapper.appendChild(dateOverlay)
    iconTd.appendChild(iconWrapper)
    icalRow.appendChild(iconTd)

    // 타이틀 셀 (getDisplayEmailHTMLStr 결과 span 을 포함하므로 HTML 파싱 이식)
    const titleTd = doc.createElement('td')
    titleTd.className = 'print-ical-title'
    appendHtmlString(doc, titleTd, icalTitle)
    icalRow.appendChild(titleTd)
    icalTbody.appendChild(icalRow)

    // 시간 행
    const timeRow = doc.createElement('tr')
    const timeTd = doc.createElement('td')
    timeTd.className = 'print-ical-info'
    timeTd.textContent = rangeStr
    timeRow.appendChild(timeTd)
    icalTbody.appendChild(timeRow)

    // 반복/장소 행
    const extraParts: string[] = []
    if (meetingInfo.recurrence) extraParts.push(meetingInfo.recurrence)
    if (meetingInfo.location) extraParts.push(meetingInfo.location)
    if (extraParts.length > 0) {
      const extraRow = doc.createElement('tr')
      const extraTd = doc.createElement('td')
      extraTd.className = 'print-ical-info'
      extraTd.textContent = extraParts.join(' | ')
      extraRow.appendChild(extraTd)
      icalTbody.appendChild(extraRow)
    }

    // 미팅 응답 버튼 행 (enableResponse && !isOwnerMeeting)
    if (hasActionsRow) {
      const actionsRow = doc.createElement('tr')
      const actionsTd = doc.createElement('td')
      actionsTd.className = 'print-ical-actions'

      const btnConfigs = [
        { cls: 'accept', label: i18n.t('common.button.accept') },
        { cls: 'decline', label: i18n.t('common.button.decline') },
        { cls: 'tentative', label: i18n.t('common.button.tentative') },
      ]

      btnConfigs.forEach(({ cls, label }) => {
        const btn = doc.createElement('span')
        btn.className = `print-ical-btn ${cls}`
        btn.textContent = label
        actionsTd.appendChild(btn)
      })

      actionsRow.appendChild(actionsTd)
      icalTbody.appendChild(actionsRow)
    }

    icalTable.appendChild(icalTbody)
    icalSection.appendChild(icalTable)
    doc.body.appendChild(icalSection)
  }

  const hr = doc.createElement('hr')
  doc.body.appendChild(hr)

  if (attachments.length > 0) {
    const attTable = doc.createElement('table')
    attTable.className = 'print-attachments'
    attTable.setAttribute('cellspacing', '0')
    attTable.setAttribute('cellpadding', '5')
    attTable.setAttribute('border', '0')
    const tbody = doc.createElement('tbody')

    // 헤더 행: "첨부파일 N개"
    const headerRow = doc.createElement('tr')
    const headerTd = doc.createElement('td')
    headerTd.setAttribute('colspan', '2')
    const headerB = doc.createElement('b')
    headerB.style.paddingLeft = '3px'
    headerB.textContent = i18n.t('email.attachment.files', { total: attachments.length })
    headerTd.appendChild(headerB)
    headerRow.appendChild(headerTd)
    tbody.appendChild(headerRow)

    // 각 첨부파일 행
    attachments.forEach((att: PrintAttachment) => {
      const name = att.name || att.id
      const size = formatBytes(att.size, 0, false) as string
      const ext = name.includes('.') ? name.substring(name.lastIndexOf('.') + 1) : ''
      const iconInfo = getImageInfo(ext)

      const row = doc.createElement('tr')
      const td = doc.createElement('td')
      const innerTable = doc.createElement('table')
      innerTable.setAttribute('cellspacing', '0')
      innerTable.setAttribute('cellpadding', '0')
      const innerTbody = doc.createElement('tbody')
      const innerRow = doc.createElement('tr')

      // 아이콘 셀
      const iconTd = doc.createElement('td')
      const iconImg = doc.createElement('img')
      iconImg.setAttribute('width', '20')
      iconImg.setAttribute('height', '20')
      iconImg.src = iconInfo.path
      iconTd.appendChild(iconImg)
      innerRow.appendChild(iconTd)

      // 간격 셀
      const spacerTd = doc.createElement('td')
      spacerTd.setAttribute('width', '7')
      innerRow.appendChild(spacerTd)

      // 파일명 + 크기 셀
      const nameTd = doc.createElement('td')
      const nameB = doc.createElement('b')
      nameB.textContent = name
      nameTd.appendChild(nameB)
      nameTd.appendChild(doc.createElement('br'))
      const sizeSpan = doc.createElement('span')
      sizeSpan.className = 'print-att-size'
      sizeSpan.textContent = String(size)
      nameTd.appendChild(sizeSpan)
      innerRow.appendChild(nameTd)

      innerTbody.appendChild(innerRow)
      innerTable.appendChild(innerTbody)
      td.appendChild(innerTable)
      row.appendChild(td)
      tbody.appendChild(row)
    })

    attTable.appendChild(tbody)
    const attSection = doc.createElement('div')
    attSection.className = 'print-section'
    attSection.appendChild(attTable)
    doc.body.appendChild(attSection)
  }

  const bodyContainer = doc.createElement('div')
  bodyContainer.className = 'print-body'
  // body HTML 은 뷰어가 sanitize 완료(의사요소 <style data-omw-pseudo> 포함)해 전달.
  appendHtmlString(doc, bodyContainer, bodyHtml)
  // 의사요소 보존 <style> 을 print 문서에서 .print-body 하위로만 적용되도록 @scope 재스코프.
  // (뷰어에서 부여한 @scope([data-email-id] .htmlMailBody) 는 print 문서에 앵커가 없으므로 교체.)
  applyPseudoStyleScope(bodyContainer, '.print-body')
  const bodySection = doc.createElement('div')
  bodySection.className = 'print-section'
  bodySection.appendChild(bodyContainer)
  doc.body.appendChild(bodySection)
}

// 인쇄 창을 열어 문서를 조립하고, 이미지 로드 대기(2s timeout) 후 인쇄한다.
// 열린 창을 반환해 host 가 unmount 시 정리(close)할 수 있게 한다.
export function openPrintWindow(options: PrintEmailOptions): Window | null {
  const pw = window.open('about:blank', '_blank', 'width=800,height=600')
  if (pw == null) {
    return null
  }

  buildPrintDocument(pw.document, options)

  const images = pw.document.querySelectorAll('img')
  const imagePromises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve()
    return new Promise<void>((resolve) => {
      img.onload = () => resolve()
      img.onerror = () => resolve()
    })
  })

  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000))

  Promise.race([Promise.all(imagePromises), timeout]).then(() => {
    if (pw.closed) return

    pw.focus()
    pw.print()
  })

  return pw
}
